import type { RegionalStoreId } from '../api/stores'
import { REGION_STORE, ZONE_REGION } from './region.codes'

/**
 * The visitor's INFERRED region, from the browser's IANA time zone. A
 * heuristic, never a verified buyer country: a traveler whose OS clock
 * follows them gets the local store, which is accepted (plan 2026-10-04,
 * section 3). Language never decides the store.
 *
 * EXACT match only, against the zone table. No prefix matching: `Europe/*`
 * would send Europe/Moscow and Europe/Istanbul to Cardmarket. Unknown zones,
 * UTC, Etc/* and any failure to read the zone all yield null.
 *
 * The value is the ISO 3166-1 code the zone belongs to (the same code set
 * iOS reads from Locale.current.region), so analytics can compare the two
 * platforms. The tables live in region.codes.ts.
 */

/** The region code for an IANA zone, or null. Pure: the unit under test. */
export function regionForTimeZone(zone: string | null | undefined): string | null {
  if (typeof zone !== 'string' || !zone) return null
  return ZONE_REGION.get(zone) ?? null
}

/** The regional store for a region code, or null for every other code. */
export function storeForRegion(region: string | null | undefined): RegionalStoreId | null {
  if (typeof region !== 'string' || !region) return null
  return REGION_STORE.get(region) ?? null
}

// undefined = not read yet; null = read, and no usable zone.
let cachedZone: string | null | undefined
let zoneOverride: string | null | undefined

function readTimeZone(): string | null {
  if (zoneOverride !== undefined) return zoneOverride
  if (cachedZone !== undefined) return cachedZone
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    cachedZone = typeof zone === 'string' && zone ? zone : null
  } catch {
    cachedZone = null
  }
  return cachedZone
}

/** The inferred ISO region code, or null. Read once per page load. */
export function inferredRegion(): string | null {
  return regionForTimeZone(readTimeZone())
}

/** The regional store this browser defaults to, or null. Says nothing about
 *  whether that store is enabled: the caller checks `/stores`. */
export function inferredRegionalStore(): RegionalStoreId | null {
  return storeForRegion(inferredRegion())
}

/** Test-only: pin the zone (a string, or null for "no zone"); undefined
 *  restores the real Intl read and forgets its cache. */
export function __setTimeZoneForTests(zone: string | null | undefined): void {
  zoneOverride = zone
  cachedZone = undefined
}
